import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const STEPS = [
  { label: 'Στοιχεία', shortLabel: 'Στοιχεία' },
  { label: 'Ραντεβού', shortLabel: 'Ραντεβού' },
  { label: 'Επιβεβαίωση', shortLabel: 'Επιβεβαίωση' },
];

const PET_TYPES = [
  { value: 'dog', label: 'Σκύλος', icon: '🐕' },
  { value: 'cat', label: 'Γάτα', icon: '🐈' },
  { value: 'other', label: 'Άλλο', icon: '🐾' },
];

// ─── Progress Indicator ───────────────────────────────────────────
const ProgressIndicator = ({ currentStep }) => {
  const progressPercent = Math.round((currentStep / STEPS.length) * 100);

  return (
    <div className="mb-6">
      {/* Step circles + labels */}
      <div className="flex items-center justify-between max-w-xs mx-auto mb-3">
        {STEPS.map((step, idx) => {
          const stepNum = idx + 1;
          const isCompleted = currentStep > stepNum;
          const isCurrent = currentStep === stepNum;
          const isFuture = currentStep < stepNum;

          return (
            <React.Fragment key={stepNum}>
              <div className="flex flex-col items-center">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                  style={{
                    background: isCompleted
                      ? 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)'
                      : isCurrent
                        ? 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)'
                        : 'rgba(55, 65, 81, 0.6)',
                    color: isFuture ? '#6B7280' : '#FFFFFF',
                    boxShadow: isCurrent ? '0 0 12px rgba(16,185,129,0.4)' : 'none',
                  }}
                >
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    stepNum
                  )}
                </div>
                <span
                  className="text-xs mt-1.5 font-medium transition-colors duration-300"
                  style={{ color: isFuture ? '#6B7280' : isCurrent ? '#10B981' : '#9CA3AF' }}
                >
                  {step.shortLabel}
                </span>
              </div>

              {/* Connector line */}
              {idx < STEPS.length - 1 && (
                <div
                  className="flex-1 h-0.5 mx-2 rounded-full transition-all duration-300 -mt-5"
                  style={{
                    background: currentStep > stepNum
                      ? 'linear-gradient(90deg, #10B981, #06B6D4)'
                      : '#374151',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#374151' }}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${progressPercent}%`,
            background: 'linear-gradient(90deg, #10B981, #06B6D4)',
          }}
        />
      </div>
    </div>
  );
};

const NewAppointmentPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [shiftAssignments, setShiftAssignments] = useState([]);
  const [shiftHourAssignments, setShiftHourAssignments] = useState([]);
  const [availableVets, setAvailableVets] = useState([]);
  const [exceptionDays, setExceptionDays] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [existingAppointments, setExistingAppointments] = useState([]);

  // Form state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedVet, setSelectedVet] = useState('');
  const [notes, setNotes] = useState('');
  const [clientFirstName, setClientFirstName] = useState('');
  const [clientLastName, setClientLastName] = useState('');
  const [clientPetName, setClientPetName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPetType, setClientPetType] = useState('');
  const [clientPetTypeOther, setClientPetTypeOther] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [stepErrors, setStepErrors] = useState({});

  // UI state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const toastTimeoutRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (clinic) {
      loadClinicData();
    }
  }, [clinic]);

  useEffect(() => {
    if (clinic && selectedDate) {
      loadAvailableShifts();
    }
  }, [clinic, selectedDate]);

  // Reset cascade: date changes → reset vet + time
  useEffect(() => {
    setSelectedVet('');
    setSelectedTime('');
  }, [selectedDate]);

  // Reset cascade: vet changes → reset time + duration
  useEffect(() => {
    setSelectedTime('');
    setSelectedDuration('');
  }, [selectedVet]);

  // Reset cascade: time changes → reset duration
  useEffect(() => {
    setSelectedDuration('');
  }, [selectedTime]);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        await loadClinic(user.id);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const loadClinic = async (userId) => {
    try {
      // First try to get clinic as owner
      let { data: clinicData, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // Not a clinic owner, check if vet
        const { data: vetData, error: vetError } = await supabase
          .from('vets')
          .select('clinic_id, clinics(*)')
          .eq('user_id', userId)
          .single();

        if (vetError) {
          displayToast('Δεν βρέθηκε κλινική. Παρακαλώ επικοινωνήστε με τον διαχειριστή.', 'error');
          return;
        }

        clinicData = vetData.clinics;
      } else if (error) {
        console.error('Error loading clinic:', error);
        displayToast('Σφάλμα κατά τη φόρτωση της κλινικής', 'error');
        return;
      }

      setClinic(clinicData);
    } catch (error) {
      console.error('Error loading clinic:', error);
      displayToast('Σφάλμα κατά τη φόρτωση της κλινικής', 'error');
    }
  };

  const loadClinicData = async () => {
    try {
      // Load clinic settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('settings_clinic_settings')
        .select('*')
        .eq('clinic_id', clinic.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        console.error('Error loading clinic settings:', settingsError);
      } else if (settingsData) {
        setClinicSettings(settingsData);
      }

      // Load exception days and special days
      const [exceptionResult, specialResult] = await Promise.all([
        supabase
          .from('settings_clinic_exception_days')
          .select('*')
          .eq('clinic_id', clinic.id),
        supabase
          .from('settings_clinic_special_days')
          .select('*')
          .eq('clinic_id', clinic.id)
      ]);

      if (exceptionResult.data) setExceptionDays(exceptionResult.data);
      if (specialResult.data) setSpecialDays(specialResult.data);
    } catch (error) {
      console.error('Error loading clinic data:', error);
      displayToast('Σφάλμα κατά τη φόρτωση των δεδομένων της κλινικής', 'error');
    }
  };

  const loadAvailableShifts = async () => {
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const jsDay = selectedDate.getDay();
      const dayOfWeek = jsDay === 0 ? 7 : jsDay; // DB uses Mon=1..Sun=7

      // Load shifts that are active for the selected date
      const { data: shiftsData, error: shiftsError } = await supabase
        .from('shifts')
        .select(`
          *,
          shift_day_groups (
            *,
            shift_days (
              weekday_id,
              settings_weekdays (*)
            ),
            shift_hours (*)
          )
        `)
        .eq('clinic_id', clinic.id)
        .eq('is_active', true)
        .lte('from_date', dateString)
        .or(`to_date.gte.${dateString},is_infinite.eq.true`);

      if (shiftsError) {
        console.error('Error loading shifts:', shiftsError);
        return;
      }

      // Filter shifts that have the selected day of week
      const filteredShifts = shiftsData?.filter(shift => {
        return shift.shift_day_groups?.some(group =>
          group.shift_days?.some(day => day.weekday_id === dayOfWeek)
        );
      }) || [];

      setAvailableShifts(filteredShifts);

      // Load vet assignments for these shifts (both shift-level and hour-level)
      if (filteredShifts.length > 0) {
        const shiftIds = filteredShifts.map(shift => shift.id);

        const [assignmentsResult, hourAssignmentsResult] = await Promise.all([
          supabase
            .from('shift_assignments')
            .select('shift_id, vet_id')
            .in('shift_id', shiftIds),
          supabase
            .from('shift_hour_assignments')
            .select('shift_id, hour_id, vet_id, weekday_id')
            .in('shift_id', shiftIds)
        ]);

        if (assignmentsResult.error) {
          console.error('Error loading shift assignments:', assignmentsResult.error);
          return;
        }
        if (hourAssignmentsResult.error) {
          console.error('Error loading hour assignments:', hourAssignmentsResult.error);
          return;
        }

        setShiftAssignments(assignmentsResult.data || []);
        setShiftHourAssignments(hourAssignmentsResult.data || []);

        // Merge vet IDs from both shift-level and hour-level assignments
        const shiftVetIds = (assignmentsResult.data || []).map(a => a.vet_id);
        const hourVetIds = (hourAssignmentsResult.data || []).map(a => a.vet_id);
        const vetIds = [...new Set([...shiftVetIds, ...hourVetIds])];

        if (vetIds.length > 0) {
          const { data: vetsData, error: vetsError } = await supabase
            .from('vets')
            .select('*')
            .in('id', vetIds)
            .eq('active', true)
            .eq('deleted', false);

          if (vetsError) {
            console.error('Error loading vets:', vetsError);
          } else {
            setAvailableVets(vetsData || []);
          }
        } else {
          setAvailableVets([]);
        }
      } else {
        setShiftAssignments([]);
        setShiftHourAssignments([]);
        setAvailableVets([]);
      }

      // Load existing appointments for this date to filter booked slots
      const { data: appointmentsData } = await supabase
        .from('appointments')
        .select('appointment_date, duration_minutes, vet_id')
        .eq('clinic_id', clinic.id)
        .gte('appointment_date', `${dateString}T00:00:00`)
        .lte('appointment_date', `${dateString}T23:59:59`);

      setExistingAppointments(appointmentsData || []);
    } catch (error) {
      console.error('Error loading available shifts:', error);
    }
  };

  // Get vets working on the selected date (from shifts + assignments)
  const getVetsForSelectedDate = () => {
    if (!availableShifts.length) return [];

    const jsDay = selectedDate.getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;
    const workingVetIds = new Set();

    availableShifts.forEach(shift => {
      // Shift-level assignments: vet works all hours of the shift
      shiftAssignments.forEach(assignment => {
        if (assignment.shift_id === shift.id) {
          workingVetIds.add(assignment.vet_id);
        }
      });

      // Hour-level assignments: vet works specific hours on specific days
      shiftHourAssignments.forEach(assignment => {
        if (assignment.shift_id === shift.id && assignment.weekday_id === dayOfWeek) {
          workingVetIds.add(assignment.vet_id);
        }
      });
    });

    return availableVets.filter(vet => workingVetIds.has(vet.id));
  };

  // Generate available time slots for a specific vet
  const generateTimeSlotsForVet = (vetId) => {
    if (!clinicSettings || !availableShifts.length) return [];

    const jsDay = selectedDate.getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const appointmentDuration = clinicSettings.appointment_duration_minutes || 30;

    // Check if this date is a closed exception day
    const isClosedException = exceptionDays.some(ex => {
      if (!ex.is_closed) return false;
      if (ex.is_yearly) {
        const exDate = new Date(ex.exception_date);
        return exDate.getMonth() === selectedDate.getMonth() && exDate.getDate() === selectedDate.getDate();
      }
      return ex.exception_date === selectedDateStr;
    });

    if (isClosedException) return [];

    // Check if this date has special hours
    const matchingSpecialDay = specialDays.find(sp => {
      if (sp.is_yearly) {
        const spDate = new Date(sp.special_date);
        return spDate.getMonth() === selectedDate.getMonth() && spDate.getDate() === selectedDate.getDate();
      }
      return sp.special_date === selectedDateStr;
    });

    const slots = [];

    if (matchingSpecialDay) {
      // Special day: all vets get these hours
      const startTime = new Date(`2000-01-01T${matchingSpecialDay.start_time}`);
      const endTime = new Date(`2000-01-01T${matchingSpecialDay.end_time}`);
      let currentTime = new Date(startTime);

      while (currentTime < endTime) {
        slots.push({ time: currentTime.toTimeString().slice(0, 5) });
        currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
      }
    } else {
      // Regular day: find which shift hours this vet works
      availableShifts.forEach(shift => {
        // Check if vet has shift-level assignment (works all hours)
        const hasShiftLevel = shiftAssignments.some(a =>
          a.shift_id === shift.id && a.vet_id === vetId
        );

        if (hasShiftLevel) {
          // Vet works all hours of matching day groups
          shift.shift_day_groups?.forEach(group => {
            const hasDay = group.shift_days?.some(d => d.weekday_id === dayOfWeek);
            if (!hasDay) return;

            group.shift_hours?.forEach(hour => {
              const startTime = new Date(`2000-01-01T${hour.start_time}`);
              const endTime = new Date(`2000-01-01T${hour.end_time}`);
              let currentTime = new Date(startTime);

              while (currentTime < endTime) {
                const timeString = currentTime.toTimeString().slice(0, 5);
                if (!slots.find(s => s.time === timeString)) {
                  slots.push({ time: timeString });
                }
                currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
              }
            });
          });
        } else {
          // Check hour-level assignments for this vet on this day
          const vetHourAssignments = shiftHourAssignments.filter(a =>
            a.shift_id === shift.id && a.vet_id === vetId && a.weekday_id === dayOfWeek
          );

          vetHourAssignments.forEach(assignment => {
            shift.shift_day_groups?.forEach(group => {
              const matchingHour = group.shift_hours?.find(h => h.id === assignment.hour_id);
              if (matchingHour) {
                const startTime = new Date(`2000-01-01T${matchingHour.start_time}`);
                const endTime = new Date(`2000-01-01T${matchingHour.end_time}`);
                let currentTime = new Date(startTime);

                while (currentTime < endTime) {
                  const timeString = currentTime.toTimeString().slice(0, 5);
                  if (!slots.find(s => s.time === timeString)) {
                    slots.push({ time: timeString });
                  }
                  currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
                }
              }
            });
          });
        }
      });
    }

    // Filter out slots already booked by this vet
    const bookedTimes = new Set();
    existingAppointments.forEach(apt => {
      if (apt.vet_id !== vetId) return;
      const aptDate = new Date(apt.appointment_date);
      const aptTime = aptDate.toTimeString().slice(0, 5);
      const aptEnd = new Date(aptDate);
      aptEnd.setMinutes(aptEnd.getMinutes() + (apt.duration_minutes || appointmentDuration));
      const aptEndTime = aptEnd.toTimeString().slice(0, 5);

      slots.forEach(slot => {
        if (slot.time >= aptTime && slot.time < aptEndTime) {
          bookedTimes.add(slot.time);
        }
      });
    });

    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    return slots
      .filter(s => !bookedTimes.has(s.time))
      .filter(s => !isToday || s.time > currentTimeStr)
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  // Generate slots for "any available" vet (union of all vets' slots)
  const generateTimeSlotsForAny = () => {
    const vetsForDate = getVetsForSelectedDate();
    const slotMap = new Map(); // time → [vetIds]

    vetsForDate.forEach(vet => {
      const vetSlots = generateTimeSlotsForVet(vet.id);
      vetSlots.forEach(slot => {
        if (!slotMap.has(slot.time)) {
          slotMap.set(slot.time, []);
        }
        slotMap.get(slot.time).push(vet.id);
      });
    });

    return [...slotMap.entries()]
      .map(([time, vetIds]) => ({ time, availableVetIds: vetIds }))
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  // Resolve which vet to use when "any" is selected and user picks a time
  const resolveVetForTime = (time) => {
    const vetsForDate = getVetsForSelectedDate();
    for (const vet of vetsForDate) {
      const vetSlots = generateTimeSlotsForVet(vet.id);
      if (vetSlots.some(s => s.time === time)) {
        return vet.id;
      }
    }
    return null;
  };

  // Calculate available duration options based on time until next appointment
  const getAvailableDurations = () => {
    if (!selectedTime || !selectedVet) return [];

    const actualVetId = selectedVet === 'any' ? resolveVetForTime(selectedTime) : selectedVet;
    if (!actualVetId) return [];

    const [hours, minutes] = selectedTime.split(':').map(Number);
    const selectedMinutes = hours * 60 + minutes;

    // Find the next appointment for this vet on this date after the selected time
    const vetAppointments = existingAppointments
      .filter(apt => apt.vet_id === actualVetId)
      .map(apt => {
        const aptDate = new Date(apt.appointment_date);
        return aptDate.getHours() * 60 + aptDate.getMinutes();
      })
      .filter(aptMinutes => aptMinutes > selectedMinutes)
      .sort((a, b) => a - b);

    const nextAptMinutes = vetAppointments.length > 0 ? vetAppointments[0] : null;
    const minutesUntilNext = nextAptMinutes !== null ? nextAptMinutes - selectedMinutes : null;

    // Also consider shift end time as a constraint
    let maxFromShift = 300; // default max

    // Find the end of the working period for this vet at the selected time
    availableShifts.forEach(shift => {
      const jsDay = selectedDate.getDay();
      const dayOfWeek = jsDay === 0 ? 7 : jsDay;

      shift.shift_day_groups?.forEach(group => {
        const hasDay = group.shift_days?.some(d => d.weekday_id === dayOfWeek);
        if (!hasDay) return;

        group.shift_hours?.forEach(hour => {
          const endTime = new Date(`2000-01-01T${hour.end_time}`);
          const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
          const startTime = new Date(`2000-01-01T${hour.start_time}`);
          const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();

          // If selected time falls within this shift hour range
          if (selectedMinutes >= startMinutes && selectedMinutes < endMinutes) {
            const available = endMinutes - selectedMinutes;
            if (available < maxFromShift) {
              maxFromShift = available;
            }
          }
        });
      });
    });

    // The max duration is the minimum of: 300, time until next appointment, time until shift ends
    let maxDuration = 300;
    if (minutesUntilNext !== null && minutesUntilNext < maxDuration) {
      maxDuration = minutesUntilNext;
    }
    if (maxFromShift < maxDuration) {
      maxDuration = maxFromShift;
    }

    // Generate options: 15, 30, 45, 60, ... up to maxDuration
    const options = [];
    for (let dur = 15; dur <= maxDuration; dur += 15) {
      options.push(dur);
    }

    return options;
  };

  // ─── Validation ───────────────────────────────────────────────

  const validateStep = (step) => {
    const errors = {};

    // Step 1: client info is optional, no validation needed
    if (step === 2) {
      if (!selectedVet) errors.selectedVet = 'Επιλέξτε κτηνίατρο';
      if (!selectedTime) errors.selectedTime = 'Επιλέξτε ώρα';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(wizardStep)) return;
    setWizardStep(prev => Math.min(prev + 1, 3));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStepErrors({});
    setWizardStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Booking ──────────────────────────────────────────────────

  const bookAppointment = async () => {
    if (!selectedTime || !selectedVet) {
      displayToast('Παρακαλώ συμπληρώστε όλα τα απαραίτητα πεδία', 'error');
      return;
    }

    // Resolve actual vet ID if "any" was selected
    const actualVetId = selectedVet === 'any' ? resolveVetForTime(selectedTime) : selectedVet;
    if (!actualVetId) {
      displayToast('Δεν βρέθηκε διαθέσιμος κτηνίατρος για αυτή την ώρα', 'error');
      return;
    }

    setSubmitting(true);

    try {
      // Check rate limit (max 20 appointments per minute to prevent spam)
      const { data: allowed, error: rateLimitError } = await supabase.rpc('check_rate_limit', {
        p_user_id: user.id,
        p_operation: 'create_appointment',
        p_limit: 20,
        p_window_minutes: 1
      });

      if (rateLimitError) {
        console.warn('Rate limit check failed:', rateLimitError);
      } else if (!allowed) {
        displayToast('Πάρα πολλές κρατήσεις! Παρακαλώ περιμένετε λίγο.', 'error');
        setSubmitting(false);
        return;
      }

      // Check plan appointment limit
      const { data: limitData, error: limitError } = await supabase.rpc('check_clinic_limit', {
        p_clinic_id: clinic.id,
        p_limit_type: 'appointments'
      });

      if (limitError) {
        console.warn('Appointment limit check failed:', limitError);
      } else if (limitData && !limitData.allowed) {
        displayToast(`Έχετε φτάσει το μηνιαίο όριο ραντεβού (${limitData.current}/${limitData.limit}). Αναβαθμίστε το πλάνο σας.`, 'error');
        setSubmitting(false);
        return;
      }

      const appointmentDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const { data: insertData, error } = await supabase
        .from('appointments')
        .insert({
          appointment_date: appointmentDateTime.toISOString(),
          duration_minutes: selectedDuration ? parseInt(selectedDuration) : (clinicSettings?.appointment_duration_minutes || 30),
          status: 'confirmed',
          notes: notes.trim() || null,
          vet_id: actualVetId,
          clinic_id: clinic.id,
          client_first_name: clientFirstName.trim() || null,
          client_last_name: clientLastName.trim() || null,
          client_pet_name: clientPetName.trim() || null,
          client_phone: clientPhone.trim() || null,
          client_email: clientEmail.trim() || null,
          client_pet_type: clientPetType === 'other' ? (clientPetTypeOther.trim() || null) : (clientPetType || null)
        })
        .select()
        .single();

      if (error) {
        console.error('Error booking appointment:', error);
        displayToast('Σφάλμα κατά την κράτηση του ραντεβού', 'error');
        setSubmitting(false);
        return;
      }

      // Log the activity (non-blocking)
      try {
        await supabase.from('activity_log').insert({
          actor_id: user.id,
          action: 'create_appointment',
          table_name: 'appointments',
          record_id: insertData?.id,
          new_values: {
            appointment_date: appointmentDateTime.toISOString(),
            vet_id: actualVetId
          }
        });
      } catch (err) {
        console.warn('Activity log failed:', err);
      }

      displayToast('Το ραντεβού κρατήθηκε επιτυχώς!', 'success');

      // Reset form
      setSelectedTime('');
      setSelectedVet('');
      setSelectedDuration('');
      setNotes('');
      setClientFirstName('');
      setClientLastName('');
      setClientPetName('');
      setClientPhone('');
      setClientEmail('');
      setClientPetType('');
      setClientPetTypeOther('');
      setWizardStep(1);
      setStepErrors({});

      // Reload available shifts
      await loadAvailableShifts();
    } catch (error) {
      console.error('Error booking appointment:', error);
      displayToast('Σφάλμα κατά την κράτηση του ραντεβού', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const displayToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    toastTimeoutRef.current = setTimeout(() => setShowToast(false), 4000);
  };

  // ─── Computed ─────────────────────────────────────────────────

  const vetsForDate = getVetsForSelectedDate();
  const timeSlotsForVet = selectedVet
    ? (selectedVet === 'any' ? generateTimeSlotsForAny() : generateTimeSlotsForVet(selectedVet))
    : [];

  const getSelectedVetName = () => {
    if (selectedVet === 'any') return 'Οποιοσδήποτε Διαθέσιμος';
    const vet = availableVets.find(v => v.id === selectedVet);
    return vet ? `${vet.first_name} ${vet.last_name}` : '';
  };

  const formatDate = (date) => {
    const days = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'];
    const months = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getDurationLabel = () => {
    const dur = selectedDuration ? parseInt(selectedDuration) : (clinicSettings?.appointment_duration_minutes || 30);
    if (dur >= 60) {
      const h = Math.floor(dur / 60);
      const m = dur % 60;
      return `${h} ώρ${h > 1 ? 'ες' : 'α'}${m > 0 ? ` ${m} λεπτά` : ''}`;
    }
    return `${dur} λεπτά`;
  };

  // ─── Inline error helper ─────────────────────────────────────

  const FieldError = ({ field }) => {
    if (!stepErrors[field]) return null;
    return (
      <p className="text-xs mt-1" style={{ color: '#EF4444' }}>
        {stepErrors[field]}
      </p>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // ─── Step Content ─────────────────────────────────────────────

  const renderStep1 = () => (
    <div
      className="rounded-xl p-5 border"
      style={{
        background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
        borderColor: '#374151',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
          <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-base lg:text-lg font-semibold text-white">Στοιχεία Πελάτη & Κατοικιδίου</h3>
        <span className="text-xs text-gray-500">(προαιρετικά)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Όνομα</label>
          <input
            type="text"
            value={clientFirstName}
            onChange={(e) => setClientFirstName(e.target.value)}
            placeholder="Όνομα πελάτη"
            className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
            style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Επώνυμο</label>
          <input
            type="text"
            value={clientLastName}
            onChange={(e) => setClientLastName(e.target.value)}
            placeholder="Επώνυμο πελάτη"
            className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
            style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Όνομα Κατοικιδίου</label>
          <input
            type="text"
            value={clientPetName}
            onChange={(e) => setClientPetName(e.target.value)}
            placeholder="Όνομα κατοικίδιου"
            className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
            style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Τηλέφωνο</label>
          <input
            type="tel"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            placeholder="Τηλέφωνο πελάτη"
            className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
            style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Email</label>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="Email πελάτη"
            className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
            style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
          />
        </div>

        {/* Pet Type Selector */}
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-400 mb-2">Τύπος Κατοικιδίου</label>
          <div className="grid grid-cols-3 gap-2">
            {PET_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  setClientPetType(type.value);
                  if (type.value !== 'other') setClientPetTypeOther('');
                }}
                className="flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 transition-all duration-200"
                style={{
                  borderColor: clientPetType === type.value ? '#10B981' : '#4A5568',
                  background: clientPetType === type.value
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(6,182,212,0.1) 100%)'
                    : 'rgba(17, 24, 39, 0.5)',
                  boxShadow: clientPetType === type.value ? '0 4px 15px rgba(16,185,129,0.2)' : 'none',
                }}
              >
                <span className="text-2xl">{type.icon}</span>
                <span className="text-xs font-medium" style={{ color: clientPetType === type.value ? '#10B981' : '#9CA3AF' }}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>

          {clientPetType === 'other' && (
            <div className="mt-2">
              <input
                type="text"
                value={clientPetTypeOther}
                onChange={(e) => setClientPetTypeOther(e.target.value)}
                placeholder="π.χ. Κουνέλι, Παπαγάλος..."
                className="w-full px-3 py-2.5 rounded-lg border text-white text-sm"
                style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Date Selection */}
      <div
        className="rounded-xl p-5 border"
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
          borderColor: '#374151',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-white">Επιλογή Ημερομηνίας</h3>
        </div>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => date && setSelectedDate(date)}
          dateFormat="dd/MM/yyyy"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={new Date()}
          maxDate={clinicSettings?.max_booking_months
            ? new Date(new Date().setMonth(new Date().getMonth() + clinicSettings.max_booking_months))
            : undefined
          }
          className="w-full px-4 py-3 rounded-lg border text-white"
          calendarClassName="dark-datepicker"
          wrapperClassName="w-full"
        />
      </div>

      {/* Vet Selection */}
      <div
        className="rounded-xl p-5 border"
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
          borderColor: stepErrors.selectedVet ? '#EF4444' : '#374151',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-white">Επιλογή Κτηνιάτρου</h3>
        </div>

        {vetsForDate.length > 0 ? (
          <div className="flex items-stretch gap-3 overflow-x-auto pb-2 -mb-2">
            {/* "Any Available" option */}
            <div
              onClick={() => setSelectedVet('any')}
              className={`flex-shrink-0 cursor-pointer transition-all duration-200 rounded-xl p-4 min-w-[100px] flex flex-col items-center justify-center border-2 ${
                selectedVet === 'any'
                  ? 'border-emerald-500 hover:border-emerald-400'
                  : 'border-gray-700 hover:border-gray-500'
              }`}
              style={{
                background: selectedVet === 'any'
                  ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(6,182,212,0.1) 100%)'
                  : 'rgba(17, 24, 39, 0.5)',
                boxShadow: selectedVet === 'any'
                  ? '0 4px 15px rgba(16,185,129,0.2)'
                  : 'none',
                transform: selectedVet === 'any' ? 'scale(1.02)' : 'scale(1)'
              }}
            >
              <div
                className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center mb-2"
                style={{ background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)' }}
              >
                <svg className="w-6 h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className={`text-xs lg:text-sm font-medium text-center ${
                selectedVet === 'any' ? 'text-emerald-400' : 'text-gray-300'
              }`}>
                Οποιοσδήποτε
              </p>
            </div>

            {/* Individual vet cards */}
            {vetsForDate.map((vet) => (
              <div
                key={vet.id}
                onClick={() => setSelectedVet(vet.id)}
                className={`flex-shrink-0 cursor-pointer transition-all duration-200 rounded-xl p-4 min-w-[100px] flex flex-col items-center justify-center border-2 ${
                  selectedVet === vet.id
                    ? 'border-emerald-500 hover:border-emerald-400'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
                style={{
                  background: selectedVet === vet.id
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(6,182,212,0.1) 100%)'
                    : 'rgba(17, 24, 39, 0.5)',
                  boxShadow: selectedVet === vet.id
                    ? '0 4px 15px rgba(16,185,129,0.2)'
                    : 'none',
                  transform: selectedVet === vet.id ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                <div
                  className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center text-lg lg:text-xl font-bold text-white mb-2"
                  style={{ background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)' }}
                >
                  {vet.first_name?.[0]}{vet.last_name?.[0]}
                </div>
                <p className={`text-xs lg:text-sm font-medium text-center max-w-[80px] truncate ${
                  selectedVet === vet.id ? 'text-emerald-400' : 'text-gray-300'
                }`}>
                  {vet.first_name}
                </p>
                <p className={`text-xs text-center max-w-[80px] truncate ${
                  selectedVet === vet.id ? 'text-emerald-400/70' : 'text-gray-500'
                }`}>
                  {vet.last_name}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">Δεν εργάζονται κτηνίατροι αυτήν την ημέρα</p>
        )}
        <FieldError field="selectedVet" />
      </div>

      {/* Available Time Slots - shown after vet is selected */}
      {selectedVet && (
        <div
          className="rounded-xl p-5 border"
          style={{
            background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
            borderColor: stepErrors.selectedTime ? '#EF4444' : '#374151',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base lg:text-lg font-semibold text-white">Διαθέσιμες Ώρες</h3>
          </div>
          {timeSlotsForVet.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {timeSlotsForVet.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedTime(slot.time)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                    selectedTime === slot.time
                      ? 'border-emerald-500 text-white'
                      : 'border-gray-700 text-gray-300 hover:border-gray-500'
                  }`}
                  style={{
                    background: selectedTime === slot.time
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(6,182,212,0.2) 100%)'
                      : 'rgba(17, 24, 39, 0.5)',
                  }}
                >
                  {slot.time}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">Δεν υπάρχουν διαθέσιμες ώρες για τον επιλεγμένο κτηνίατρο</p>
          )}
          <FieldError field="selectedTime" />
        </div>
      )}

      {/* Duration Selection - shown after time is selected */}
      {selectedTime && (
        <div
          className="rounded-xl p-5 border"
          style={{
            background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
            borderColor: '#374151',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base lg:text-lg font-semibold text-white">Διάρκεια Ραντεβού</h3>
          </div>
          {getAvailableDurations().length > 0 ? (
            <select
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border text-white"
              style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
            >
              <option value="">Προεπιλογή ({clinicSettings?.appointment_duration_minutes || 30} λεπτά)</option>
              {getAvailableDurations().map((dur) => (
                <option key={dur} value={dur}>
                  {dur >= 60 ? `${Math.floor(dur / 60)} ώρ${Math.floor(dur / 60) > 1 ? 'ες' : 'α'}${dur % 60 > 0 ? ` ${dur % 60} λεπτά` : ''}` : `${dur} λεπτά`}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-gray-400">Δεν υπάρχουν διαθέσιμες επιλογές διάρκειας</p>
          )}
        </div>
      )}

      {/* Notes */}
      <div
        className="rounded-xl p-5 border"
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
          borderColor: '#374151',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-white">Σημειώσεις</h3>
          <span className="text-xs text-gray-500">(προαιρετικά)</span>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Προαιρετικές σημειώσεις..."
          rows="3"
          maxLength={500}
          className="w-full px-3 py-2.5 rounded-lg border text-white text-sm resize-none"
          style={{ backgroundColor: '#1A202C', borderColor: '#4A5568' }}
        />
        <p className="text-gray-500 text-xs mt-1">{notes.length}/500</p>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Summary Card */}
      <div
        className="rounded-xl p-5 border"
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
          borderColor: '#374151',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
        }}
      >
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-white">Σύνοψη Ραντεβού</h3>
        </div>

        <div className="space-y-3">
          {/* Client Name (if provided) */}
          {(clientFirstName.trim() || clientLastName.trim()) && (
            <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
              <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Πελάτης</p>
                <p className="text-sm font-medium text-white">{[clientFirstName.trim(), clientLastName.trim()].filter(Boolean).join(' ')}</p>
              </div>
            </div>
          )}

          {/* Pet Name (if provided) */}
          {(clientPetName.trim() || clientPetType) && (
            <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
              <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Κατοικίδιο</p>
                <p className="text-sm font-medium text-white">
                  {clientPetName.trim()}
                  {clientPetType && (
                    <span className="font-normal text-gray-400 ml-1">
                      ({clientPetType === 'other'
                        ? clientPetTypeOther.trim()
                        : PET_TYPES.find(p => p.value === clientPetType)?.icon + ' ' + PET_TYPES.find(p => p.value === clientPetType)?.label})
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Date & Time */}
          <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
            <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Ημερομηνία & Ώρα</p>
              <p className="text-sm font-medium text-white">{formatDate(selectedDate)}</p>
              <p className="text-sm text-emerald-400 font-medium">{selectedTime}</p>
            </div>
          </div>

          {/* Vet */}
          <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
            <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Κτηνίατρος</p>
              <p className="text-sm font-medium text-white">{getSelectedVetName()}</p>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
            <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Διάρκεια</p>
              <p className="text-sm font-medium text-white">{getDurationLabel()}</p>
            </div>
          </div>

          {/* Contact info (if provided) */}
          {(clientPhone.trim() || clientEmail.trim()) && (
            <div className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: 'rgba(55, 65, 81, 0.5)' }}>
              <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Στοιχεία Επικοινωνίας</p>
                {clientPhone.trim() && <p className="text-sm text-white">{clientPhone}</p>}
                {clientEmail.trim() && <p className="text-sm text-gray-400">{clientEmail}</p>}
              </div>
            </div>
          )}

          {/* Notes if present */}
          {notes.trim() && (
            <div className="flex items-start gap-3 py-2.5">
              <div className="p-1.5 rounded-md mt-0.5" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Σημειώσεις</p>
                <p className="text-sm text-gray-300">{notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ─── Navigation Buttons ───────────────────────────────────────

  const renderNavButtons = () => {
    if (wizardStep === 1) {
      return (
        <button
          onClick={handleNext}
          className="w-full px-6 py-3.5 rounded-xl font-medium text-white transition-all duration-200 text-sm"
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
            boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
          }}
        >
          Συνέχεια
        </button>
      );
    }

    if (wizardStep === 2) {
      return (
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 px-6 py-3.5 rounded-xl font-medium transition-all duration-200 text-sm border"
            style={{
              color: '#D1D5DB',
              borderColor: '#4A5568',
              backgroundColor: 'transparent',
            }}
          >
            Πίσω
          </button>
          <button
            onClick={handleNext}
            className="flex-[2] px-6 py-3.5 rounded-xl font-medium text-white transition-all duration-200 text-sm"
            style={{
              background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
              boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
            }}
          >
            Συνέχεια
          </button>
        </div>
      );
    }

    if (wizardStep === 3) {
      return (
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 px-6 py-3.5 rounded-xl font-medium transition-all duration-200 text-sm border"
            style={{
              color: '#D1D5DB',
              borderColor: '#4A5568',
              backgroundColor: 'transparent',
            }}
          >
            Πίσω
          </button>
          <button
            onClick={bookAppointment}
            disabled={submitting}
            className="flex-[2] px-6 py-3.5 rounded-xl font-medium text-white transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: submitting
                ? '#4A5568'
                : 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
              boxShadow: submitting ? 'none' : '0 4px 15px rgba(16,185,129,0.3)',
            }}
          >
            {submitting ? 'Κράτηση...' : 'Επιβεβαίωση Ραντεβού'}
          </button>
        </div>
      );
    }
  };

  // ─── Main Render ──────────────────────────────────────────────

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row pb-28 sm:pb-8" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast Notification */}
        {showToast && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toastType === 'success' ? '#10B981' : toastType === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toastType === 'success' ? 'rgba(16,185,129,0.2)' :
                  toastType === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              {toastType === 'success' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(16,185,129,0.2)' }}>
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {toastType === 'error' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(239,68,68,0.2)' }}>
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              {toastType === 'info' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(59,130,246,0.2)' }}>
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
              <span className="text-white font-medium flex-1">{toastMessage}</span>
              <button
                onClick={() => setShowToast(false)}
                className="ml-3 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Νέο Ραντεβού</h1>
          <p className="text-gray-400 text-base lg:text-lg">
            Κράτηση νέου ραντεβού
          </p>
        </div>

        {!clinic ? (
          <div className="rounded-lg overflow-hidden mb-6" style={{ backgroundColor: '#2D3748' }}>
            <div className="p-8 text-center">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Δεν βρέθηκε κλινική</h3>
              <p className="text-gray-400">
                Για να χρησιμοποιήσετε τη λειτουργικότητα του ραντεβού, πρέπει πρώτα να δημιουργήσετε την κλινική σας.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            {/* Progress Indicator */}
            <ProgressIndicator currentStep={wizardStep} />

            {/* Step Content */}
            <div className="space-y-4">
              {wizardStep === 1 && renderStep1()}
              {wizardStep === 2 && renderStep2()}
              {wizardStep === 3 && renderStep3()}

              {/* Desktop navigation buttons (visible on sm+) */}
              <div className="hidden sm:block">
                {renderNavButtons()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom navigation for mobile */}
      {clinic && (
        <div
          className="fixed bottom-0 left-0 right-0 sm:hidden z-40 px-4 py-3 border-t"
          style={{
            background: 'linear-gradient(to top, #1A202C 0%, rgba(26,32,44,0.98) 100%)',
            borderColor: '#374151',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
          }}
        >
          {renderNavButtons()}
        </div>
      )}

      {/* Mobile overlay when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default NewAppointmentPage;
